import { CellCoordsObj, CellSelection, CellSelections } from '../../../../../common/table/TableTypes';
import { getFirstSelection, getEndCol, getEndRow, getStartCol, getStartRow } from './tableSelectionAccessors';

/**
 * Returns whether the selection starts from the column header reference row.
 */
export const isSelectingRefColHeader = (selection: CellSelection): boolean => getStartRow(selection) === -1;
/**
 * Returns whether the selection starts from the row header reference column.
 */
export const isSelectingRefRowHeader = (selection: CellSelection): boolean => getStartCol(selection) === -1;

/**
 * Returns whether the selection touches the first data row.
 */
export const isSelectingFirstRow = (selection: CellSelection): boolean =>
    getStartRow(selection) === 0 || getEndRow(selection) === 0;

/**
 * Returns whether the selection touches the last data row.
 */
export const isSelectingLastRow = (selection: CellSelection, nRows: number): boolean =>
    getStartRow(selection) === nRows - 1 || getEndRow(selection) === nRows - 1;

/**
 * Returns whether the selection resolves to a single cell.
 */
export const isSelectingSingleCell = (selection: CellSelection): boolean =>
    getStartRow(selection) === getEndRow(selection) && getStartCol(selection) === getEndCol(selection);

/**
 * Returns whether one selection is fully contained within another.
 */
export const isSelectionSubsetOf = (selection: CellSelection, largeSelection: CellSelection): boolean =>
    getStartRow(largeSelection) <= getStartRow(selection) &&
    getStartCol(largeSelection) <= getStartCol(selection) &&
    getEndRow(largeSelection) >= getEndRow(selection) &&
    getEndCol(largeSelection) >= getEndCol(selection);

/**
 * Returns whether a row index falls within the selection bounds.
 */
export const isRowInSelection = (row: number, cellSelection: CellSelection): boolean =>
    (row >= getStartRow(cellSelection) && row <= getEndRow(cellSelection)) ||
    (row >= getEndRow(cellSelection) && row <= getStartRow(cellSelection));

/**
 * Returns whether a column index falls within the selection bounds.
 */
export const isColInSelection = (col: number, cellSelection: CellSelection): boolean =>
    (col >= getStartCol(cellSelection) && col <= getEndCol(cellSelection)) ||
    (col >= getEndCol(cellSelection) && col <= getStartCol(cellSelection));

/**
 * Returns whether any selection contains the given row index.
 */
export const isRowInSelections = (row: number, cellSelections: CellSelections): boolean =>
    cellSelections.some((cellSelection: CellSelection) => isRowInSelection(row, cellSelection));

/**
 * Returns whether any selection contains the given column index.
 */
export const isColInSelections = (col: number, cellSelections: CellSelections): boolean =>
    cellSelections.some((cellSelection: CellSelection) => isColInSelection(col, cellSelection));

/**
 * Returns whether a coordinate points to the column header reference row.
 */
export const isCellRefColHeader = ({ row }: CellCoordsObj): boolean => row === -1;
/**
 * Returns whether a coordinate points to the row header reference column.
 */
export const isCellRefRowHeader = ({ col }: CellCoordsObj): boolean => col === -1;

/**
 * Returns whether there is at least one active selection.
 */
export const hasSelections = (cellSelections: CellSelections): boolean => !!cellSelections && cellSelections.length > 0;

/**
 * Returns whether there is exactly one active selection.
 */
export const isSelectingSingleSelection = (cellSelections: CellSelections): boolean => cellSelections?.length === 1;

/**
 * Returns whether there is exactly one selection and it is a single cell.
 */
export const isSelectingSingleSelectionAndCell = (cellSelections?: CellSelections): boolean => {
    if (cellSelections?.length !== 1) return false;

    const firstSelection = getFirstSelection(cellSelections);
    return !!firstSelection && isSelectingSingleCell(firstSelection);
};

/**
 * Returns whether there is more than one active selection.
 */
export const isSelectingMultipleSelections = (cellSelections: CellSelections | undefined): boolean =>
    !!cellSelections && cellSelections.length > 1;
